import type { BankFeedAccountType, BankFeedSummary } from 'domain/bankFeed/schemas';

export type EmptyStateVariant =
  | 'no-connections'
  | 'no-transactions'
  | 'connecting'
  | 'no-filter-results';

// The pre-data variants this selector can return. `no-filter-results` is a
// post-data state (decided by AG Grid's row count, not the summary), so it is
// deliberately excluded — a non-null return here always means "show a pre-data
// empty state and disable the toolbar filters", which would be wrong for
// `no-filter-results`.
export type PreDataEmptyVariant = Extract<
  EmptyStateVariant,
  'no-connections' | 'no-transactions' | 'connecting'
>;

// Pre-data empty-state selector. Pure: depends only on the summary + active
// account type, so filter components can consume it without pulling in the
// EmptyState UI module (router / i18n / MUI / SVG). Returns null when the grid
// should mount; the post-data NoFilterResults overlay is handled by AG Grid's
// noRowsOverlay slot once the grid fires getRows and resolves with rowCount === 0.
//
// Returns null (mount the grid) when `summary == null` (still loading or errored —
// the LinearProgress / error toast cover that UX) and when the active tab has no
// summary item (a malformed response, surfaced as an error toast in
// useBankFeedSummary). We never fabricate an empty-state variant without a concrete
// `state`.
export const computeEmptyVariant = (
  summary: BankFeedSummary | null | undefined,
  accountType: BankFeedAccountType,
): PreDataEmptyVariant | null => {
  if (summary == null) return null;
  const activeItem = summary.items.find((i) => i.accountType === accountType);
  if (activeItem == null) return null;
  switch (activeItem.state) {
    case 'NO_CONNECTIONS':
      return 'no-connections';
    case 'NO_TRANSACTIONS':
      return 'no-transactions';
    case 'CONNECTING':
      return 'connecting';
    case 'CONNECTED':
    default:
      return null;
  }
};
